import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const MS_PER_DAY = 24 * 3600 * 1000;
const startOfUTCDay = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
const isoUTC = (ms) => new Date(ms).toISOString();
const todayUTC = () => startOfUTCDay(new Date());
const lastNDaysInclusive = (n) => {
    const endUTC = todayUTC();
    const startUTC = endUTC - (n - 1) * MS_PER_DAY;
    return { startIso: isoUTC(startUTC), endIso: isoUTC(endUTC) };
};
const isoToYMD = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};
const ymdToIsoUTC = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
    return isoUTC(Date.UTC(y, (m || 1) - 1, d || 1));
};

const PRESETS = [
    { key: "1m",  days: 30 },
    { key: "3m",  days: 90 },
    { key: "6m",  days: 180 },
    { key: "1y",  days: 365 },
    { key: "max", days: 365 * 3 },
];

/**
 * Presets and a custom range. A preset is `{ key, days }`, the last `days` days to today, or
 * `{ key, label, startIso, endIso }` when the caller sets its range. `minDate` and `maxDate`
 * (YYYY-MM-DD), when set, are the earliest and latest days a custom range can start or end on.
 */
export default function DateRangeSelector({ startDate, endDate, preset = "max", onChange, presets = PRESETS, minDate, maxDate, className }) {
    const [mode, setMode] = useState(preset);
    const [customStart, setCustomStart] = useState(() => isoToYMD(startDate));
    const [customEnd,   setCustomEnd]   = useState(() => isoToYMD(endDate));

    useEffect(() => {
        setMode(preset);
        setCustomStart(isoToYMD(startDate));
        setCustomEnd(isoToYMD(endDate));
    }, [preset, startDate, endDate]);

    const customDisabled = useMemo(() => {
        const sIso = ymdToIsoUTC(customStart);
        const eIso = ymdToIsoUTC(customEnd);
        return !sIso || !eIso || new Date(sIso) > new Date(eIso) || Boolean(minDate && customStart < minDate) || Boolean(maxDate && customEnd > maxDate);
    }, [customStart, customEnd, minDate, maxDate]);

    const applyPreset = (p) => {
        const found = presets.find(x => x.key === p);
        if (!found) return;
        setMode(p);
        const { startIso, endIso } = found.startIso ? found : lastNDaysInclusive(found.days);
        onChange({ startIso, endIso, preset: p });
        setCustomStart(isoToYMD(startIso));
        setCustomEnd(isoToYMD(endIso));
    };

    const applyCustom = () => {
        const sIso = ymdToIsoUTC(customStart);
        const eIso = ymdToIsoUTC(customEnd);
        if (!sIso || !eIso) return;
        onChange({ startIso: sIso, endIso: eIso, preset: "custom" });
    };

    return (
        <Wrap className={className}>
            <PresetRow role="group" aria-label="Date range">
                {presets.map(p => (
                    <PresetBtn key={p.key} $active={mode === p.key} aria-pressed={mode === p.key} onClick={() => applyPreset(p.key)}>
                        {p.label || p.key}
                    </PresetBtn>
                ))}
                <PresetBtn $active={mode === "custom"} aria-pressed={mode === "custom"} onClick={() => setMode("custom")}>
                    Custom
                </PresetBtn>
            </PresetRow>
            {mode === "custom" && (
                <CustomRow>
                    <DateInput
                        type="date"
                        value={customStart}
                        min={minDate}
                        max={maxDate}
                        onChange={e => setCustomStart(e.target.value)}
                    />
                    <DateSep>–</DateSep>
                    <DateInput
                        type="date"
                        value={customEnd}
                        min={minDate}
                        max={maxDate}
                        onChange={e => setCustomEnd(e.target.value)}
                    />
                    <ApplyBtn onClick={applyCustom} disabled={customDisabled}>Apply</ApplyBtn>
                </CustomRow>
            )}
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

export const PresetRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 3px;
`;

export const PresetBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 9px;
    border-radius: 5px;
    border: none;
    background: ${({ $active }) => $active ? '#6366f1' : 'transparent'};
    color: ${({ $active }) => $active ? '#fff' : '#64748b'};
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms, color 120ms;
    &:hover { background: ${({ $active }) => $active ? '#6366f1' : '#ede9fe'}; color: ${({ $active }) => $active ? '#fff' : '#6366f1'}; }
`;

const CustomRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const DateInput = styled.input`
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 3px 8px;
    font-size: 0.75rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    &:focus { border-color: #a5b4fc; background: #fff; }
`;

const DateSep = styled.span`
    font-size: 0.75rem;
    color: #94a3b8;
`;

const ApplyBtn = styled.button`
    padding: 3px 10px;
    border-radius: 5px;
    border: none;
    background: #6366f1;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: #4f46e5; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
